import { create } from 'zustand';
import { AudioPlayer } from '@/audio/playback';
import {
  encodeFrame,
  frameLevel,
  MIC_BITS_PER_SAMPLE,
  MIC_CHANNELS,
  MIC_ENCODING,
  MIC_SAMPLE_RATE,
  startMicCapture,
  type MicStream,
} from '@/audio/capture';
import {
  asVoiceMode,
  isKnownFrameType,
  parseFrame,
  summarizeFrame,
  type DeviceMode,
  type InboundFrame,
  type OutboundFrame,
  type SignInDetails,
  type VoiceMode,
} from '@/protocol/frames';
import type { ChoicePrompt, SearchResults } from '@/protocol/media';
import { toPayloadBlock, type ToolPayloadBlock } from '@/protocol/payloads';
import { deployedEndpoint, loadSettings, resolveEndpoint, saveSettings } from './config';
import { DEFAULT_MODEL_ID, formatModelId, parseModelId } from './models';
import { selectionFromStored, ttsSummary, type ProviderChoice, type TtsValue } from './tts';
import { catalogUrl, fetchTtsCatalog, type TtsCatalog } from './ttsCatalog';
import {
  CLIENT_EVENTS,
  EMPTY_DEBUG_STATE,
  noteResponseStream,
  recordClient,
  recordServer,
  type DebugState,
} from './debug';

export type ConnectionState = 'idle' | 'connecting' | 'open';

export interface TextMessage {
  id: string;
  role: 'user' | 'assistant';
  kind: 'text';
  text: string;
}

export interface ResultsMessage {
  id: string;
  role: 'assistant';
  kind: 'results';
  results: SearchResults;
}

export interface ChoiceMessage {
  id: string;
  role: 'assistant';
  kind: 'choice';
  choice: ChoicePrompt;
}

export type Message = TextMessage | ResultsMessage | ChoiceMessage;

export interface FrameLogEntry {
  id: string;
  direction: 'in' | 'out';
  type: string;
  /** False for a frame type this client has no handler for — it was logged and dropped. */
  known: boolean;
  atMs: number;
  body: string;
}

/**
 * Enough to cover a long test session; unbounded would grow without limit on a voice socket. A turn
 * is dozens of frames, reply text arriving in chunks as the model produces it, so this is counted in
 * turns rather than in messages: roughly a hundred of them.
 */
const FRAME_LOG_LIMIT = 5000;

interface SessionState {
  connection: ConnectionState;
  deviceMode: DeviceMode | null;
  sessionId: string | null;
  /** As typed: a `wss://…` URL or a same-origin path. Resolved only when the socket opens. */
  endpoint: string;
  signIn: SignInDetails;
  /**
   * The voice experience display mode: what the client asks for at sign-in, and — once a session
   * is open — what it is currently in. One field for both, because the contract treats them as one
   * thing: Andromeda can redirect it mid-session with `changeVoiceState`, and the client reports it
   * back on `screenState` whenever it changes. Editable in the debug drawer before a session opens;
   * driven by the server once one is.
   */
  voiceMode: VoiceMode;
  /** Purely local render policy, so it takes effect on the next interruption. */
  dropAbandonedText: boolean;
  /**
   * Which LLM answers, as `provider:model`, or `andromeda-default` to name none (see
   * session/models.ts). Named in the handshake, so an edit only takes effect on the next sign-in —
   * {@link SessionState.sessionModelId} is what the open session is actually running.
   */
  modelId: string;
  /**
   * The model the open session signed in on, or null when there is no session. Separate from
   * {@link SessionState.modelId} so the selector stays editable during a session — picking a model
   * and then reconnecting is the whole workflow — without the top bar claiming the running session
   * is on a model it never signed in with.
   */
  sessionModelId: string | null;

  /**
   * The TTS provider this session speaks through, or `andromeda-default` to name none (see
   * session/tts.ts). Set together with the model and voice, as one row of the voice picker. Named in
   * the handshake, so an edit only takes effect on the next sign-in — {@link SessionState.sessionTts}
   * is what the open session actually speaks with.
   */
  ttsProvider: ProviderChoice;
  /** The TTS model, where the provider has one; empty for Polly and the backend default. */
  ttsModel: string;
  /** The TTS voice the chosen provider offers; empty for the backend default. */
  ttsVoice: string;
  /**
   * The provider/model/voice catalogue, fetched live from the backend's `/tts/catalog`. Empty
   * until it loads; the picker reads it, so it offers exactly what the pointed-at deployment can
   * speak with rather than anything baked into this build.
   */
  ttsCatalog: TtsCatalog;
  /** Where the catalogue fetch is: not started, in flight, loaded, or failed. */
  ttsCatalogStatus: 'idle' | 'loading' | 'ready' | 'error';
  /** On failure, the URL that was tried and the reason — shown so the cause is self-evident. */
  ttsCatalogError: string | null;
  /**
   * A one-line summary of what the open session signed in to speak with, or null when there is no
   * session. Separate from the three editable fields so the selector stays editable during a session
   * without the readout claiming the running session speaks with a voice it never signed in with.
   */
  sessionTts: string | null;

  messages: Message[];
  /**
   * Whether the session's opening prompt — the one the empty transcript shows above the composer —
   * has been spent. Latched rather than derived from `micArmed` or the composer's draft: it is the
   * *first* interaction that retires the prompt, and a derived flag would bring it back the moment
   * the key is released or the field is cleared, which reads as the console losing its place.
   */
  introDismissed: boolean;
  streaming: boolean;
  thinking: boolean;
  thinkingText: string;
  thinkingIcons: string[];
  transcript: string | null;

  muted: boolean;
  micArmed: boolean;
  micLive: boolean;
  micLevel: number;

  error: string | null;
  debug: DebugState;
  frames: FrameLogEntry[];
  /** How many frames the log has dropped to stay bounded, so an export can say so. */
  framesDropped: number;
  audioFramesSent: number;
  /** Frame types the server sent that this client has no handler for. */
  unknownFrameTypes: string[];
  /** Tool payload types that arrived but map to no render template here. */
  unknownPayloadTypes: string[];

  // Declared as properties rather than methods: they are plain functions in the slice, never
  // called with a `this`, and selecting one (`useSession((s) => s.send)`) must stay safe.
  connect: (mode: DeviceMode) => void;
  reconnect: () => void;
  /** Closes the session and returns the console to the start screen. */
  leaveSession: () => void;
  send: (text: string) => void;
  /** Retires the opening prompt, on the first thing the user says or types. */
  dismissIntro: () => void;
  sendFrame: (frame: OutboundFrame) => void;
  setEndpoint: (endpoint: string) => void;
  resetEndpoint: () => void;
  patchSignIn: (key: keyof SignInDetails, value: string) => void;
  setVoiceMode: (voiceMode: VoiceMode) => void;
  setDropAbandonedText: (dropAbandonedText: boolean) => void;
  setModelId: (modelId: string) => void;
  /** Picks the TTS provider, model and voice together, as one row of the voice picker. */
  setTts: (value: TtsValue) => void;
  /** Fetches the TTS catalogue from the current endpoint, for the voice picker. */
  loadTtsCatalog: () => void;
  toggleMute: () => void;
  dismissError: () => void;
  clearFrames: () => void;
  startMic: () => void;
  stopMic: () => void;
}

const player = new AudioPlayer();

/** Everything that is a live handle rather than rendered state. */
let socket: WebSocket | null = null;
let micStream: MicStream | null = null;
let audioChunkNumber = 0;
/**
 * The loudest sample seen anywhere in the utterance being spoken. A device can be open, live and
 * unmuted and still deliver nothing but zeros — a stale per-origin device pin in the browser is
 * enough — and the gateway answers such an utterance with silence of its own, having had nothing to
 * recognize. Tracking the peak is what lets {@link stopMic} say so instead of leaving a turn that
 * looks sent and never comes back.
 */
let utterancePeak = 0;
/**
 * The interaction id every frame of the utterance being spoken carries. Minted on the press rather
 * than when the transcript settles, so the turn is identified from its first frame — which is what
 * the gateway needs in order to answer under the same id.
 */
let micTurnId: number | null = null;
let currentResponse = '';
/** True from the first transcription of an utterance until the reply starts streaming. */
let transcribing = false;
/**
 * The turn the conversation is currently being written from: the id sent with the newest request,
 * whether typed or spoken. A frame naming any other turn belongs to a reply that has been talked
 * over, and is not allowed to write into the one that replaced it.
 */
let activeTurnId: number | null = null;
/**
 * Whether this gateway says which turn its frames belong to. Latched on the first frame that carries
 * an id rather than configured, since the console is pointed at more than one backend and only some
 * of them echo it. Until then attribution falls back to counting, below.
 */
let turnIdsEchoed = false;
/**
 * How many end-of-response markers belong to replies that have been talked over — the fallback for a
 * gateway that echoes no turn id, where a frame cannot be attributed at all. Every turn sends exactly
 * one marker, so counting them is enough to know which frames still belong to the abandoned reply.
 * Weaker than the id in two ways that matter: it assumes markers arrive in turn order, which the
 * deployed backend does not guarantee, and it can say a frame is stale without saying which turn it
 * came from.
 */
let abandonedTurns = 0;
/**
 * Whether a reply has been asked for and not yet ended. Tracked separately from `streaming`, which is
 * render state that the drop policy is allowed to disturb — an abandoned turn's end marker clears it
 * when the tail is being rendered — where what decides whether to silence the speaker must not move
 * with it. Between that marker and the new reply's first chunk the model and the guardrail take
 * seconds, and reading `streaming` there would leave the interruption doing nothing at all.
 */
let replyInFlight = false;
let seq = 0;

const nextId = () => `m${++seq}`;

/**
 * The ordinal of the next user interaction in this session. Module state rather than store state:
 * nothing renders it, and every reader of it is in this file.
 *
 * Reset to zero when the gateway reports a new session, not when the socket opens — the numbering
 * belongs to the conversation the server keeps, so a reconnect that resumed one would go on counting
 * from where it left off. Today the client never asks to resume (`sessionIdToResume: ''`), so in
 * practice every sign-in starts a new session and the reset happens on each connect anyway.
 */
let nextTurn = 0;
/**
 * The session `nextTurn` is counting in. Kept here rather than read off the store, which clears its
 * session id when the socket closes — and a resumed session is precisely the case where the id
 * outlives the connection.
 */
let numberedSessionId: string | null = null;

/**
 * The id for one user interaction: its ordinal in the session, counted from zero. The contract has
 * the client generate it, so that the request, everything the server sends back in answer, and the
 * log lines in between all name the same turn.
 *
 * Post-increment, so the session's first turn is turn 0 — which makes every "is there an id here"
 * test a comparison against null rather than a truthiness check.
 */
const newTurnId = () => nextTurn++;

/**
 * A turn id as the debug model keys turns by, which is text: it indexes turns in an object, and the
 * ids it sees come from the wire, where a gateway may echo the ordinal as either a number or a
 * string. Normalizing here keeps that one shape out of `debug.ts`.
 */
const turnKey = (turnId: string | number | null | undefined): string | undefined =>
  turnId == null ? undefined : String(turnId);

const initial = loadSettings();

export const useSession = create<SessionState>((set, get) => {
  /** Appends to the frame log, dropping the oldest once it is full. */
  function logFrame(direction: 'in' | 'out', type: string, known: boolean, body: string): void {
    set((state) => {
      const entry: FrameLogEntry = { id: nextId(), direction, type, known, atMs: Date.now(), body };
      const frames = [...state.frames, entry];
      if (frames.length <= FRAME_LOG_LIMIT) {
        return { frames };
      }
      // Counted, not merely dropped: an exported trace that silently began in the middle reads as
      // the whole session.
      const dropped = frames.length - FRAME_LOG_LIMIT;
      return {
        frames: frames.slice(dropped),
        framesDropped: state.framesDropped + dropped,
      };
    });
  }

  /**
   * Opens the bubble the reply streams into. Called from {@link send} for a typed turn (with the
   * user's message appended first) and from the chunk handler for a spoken turn, whose first
   * reply chunk is what creates it.
   */
  function beginAssistantTurn(userText?: string): void {
    transcribing = false;
    currentResponse = '';
    replyInFlight = true;

    set((state) => ({
      streaming: true,
      thinking: false,
      thinkingText: 'Thinking',
      thinkingIcons: [],
      transcript: null,
      messages: [
        ...state.messages,
        ...(userText !== undefined
          ? [{ id: nextId(), role: 'user' as const, kind: 'text' as const, text: userText }]
          : []),
        { id: nextId(), role: 'assistant' as const, kind: 'text' as const, text: '' },
      ],
    }));
  }

  /** Rewrites the open assistant bubble. Not necessarily the tail: a shelf can already follow it. */
  function writeAssistantText(text: string): void {
    set((state) => {
      const messages = [...state.messages];
      for (let i = messages.length - 1; i >= 0; i--) {
        const message = messages[i];
        if (message.role === 'assistant' && message.kind === 'text') {
          messages[i] = { ...message, text };
          break;
        }
      }
      return { messages };
    });
  }

  function endTurn(): void {
    currentResponse = '';
    set({ streaming: false, thinking: false });
  }

  /**
   * Gives up on the reply in flight, from this side, however the user interrupted it: silences the
   * speech already scheduled, closes the turn being rendered, and marks its tail — the frames the
   * gateway had already sent when the interruption reached it — as no longer part of the
   * conversation. Doing all three together is what keeps text and speech agreeing about where the
   * reply stopped.
   *
   * The turn has to be closed here rather than on its end marker, because that marker is itself part
   * of the tail now being dropped.
   */
  function abandonStreamingReply(): void {
    if (!replyInFlight) return;

    player.stop();
    abandonedTurns++;
    endTurn();
  }

  /**
   * Whether a frame belongs to a reply that has already been talked over.
   *
   * An id settles it outright. Without one there is nothing in the frame to go on, so the count of
   * outstanding abandoned turns decides it, and this consumes a marker as it reports it: that marker
   * is the last frame of the reply it was counted for.
   *
   * @param turnId the turn the gateway attributed this frame to, if it attributed it at all
   * @param endsATurn whether this frame is a turn's end marker
   */
  function staleTurn(turnId: string | number | undefined, endsATurn: boolean): boolean {
    // Compared as text, not by value: turn 0 is a real turn, and a gateway that echoes the ordinal
    // as a string is still naming the turn we sent.
    if (turnId != null) {
      return activeTurnId !== null && String(turnId) !== String(activeTurnId);
    }
    // Unattributed on a gateway that attributes everything else — an error raised outside any turn.
    // It belongs to the session, so it is nobody's tail.
    if (turnIdsEchoed) return false;

    if (abandonedTurns === 0) return false;
    if (endsATurn) abandonedTurns--;
    return true;
  }

  function handleFrame(frame: InboundFrame): void {
    const now = Date.now();

    // The tail of a reply that has been talked over: the gateway had already sent these when the
    // interruption reached it. They are dropped rather than rendered, or the abandoned reply writes
    // its last words into the bubble belonging to the reply that replaced it — and its end marker
    // ends that reply before it has said anything. The frame log still shows them arriving, which is
    // what the wire did; this only decides what the conversation is allowed to read.
    //
    // Attribution runs either way, so `dropAbandonedText` can be turned off to watch what a client
    // that renders the tail actually does — the STB's intended behaviour — and back on again
    // mid-session without the bookkeeping having drifted in between.
    if (frame.turnId != null) turnIdsEchoed = true;
    const endsATurn = frame.type === 'modelResponseChunk' && frame.chunkNumber === -1;
    const stale = staleTurn(frame.turnId, endsATurn);

    if (stale) {
      if (get().dropAbandonedText && frame.type !== 'debug') return;
    } else if (endsATurn) {
      // Only a marker that is not an abandoned turn's ends the reply that is actually in flight.
      replyInFlight = false;
    }

    switch (frame.type) {
      case 'signInResponse': {
        if (frame.success) {
          // A session the server just opened numbers its turns from zero; one it resumed goes on
          // from where the previous socket left off, so the counter is only reset when this is
          // actually a different conversation. A gateway that names no session is taken at its word
          // as a new one — there is nothing to resume against.
          const sessionId = frame.sessionId ?? null;
          if (frame.newSession || sessionId === null || sessionId !== numberedSessionId) {
            nextTurn = 0;
          }
          numberedSessionId = sessionId;
          set({ connection: 'open', sessionId, error: null });
        } else {
          set({
            connection: 'idle',
            error: frame.errorMessage || 'Sign-in was refused by the gateway.',
          });
          teardown();
        }
        return;
      }

      // Diagnostics. Ignorable by contract, and never allowed to disturb the conversation, so it
      // is handled before anything reads a frame as content.
      case 'debug': {
        set((state) => ({
          debug: recordServer(
            state.debug,
            frame.data?.description ?? 'unknown',
            frame.data?.content,
            frame.timestampMs,
            now,
            turnKey(frame.turnId),
          ),
        }));
        return;
      }

      case 'audioMessage': {
        // The end-of-audio marker is not sound: there is nothing to play, and a turn that spoke
        // nothing sends it too, so stamping it as the reply's first frame would time a reply that
        // never started.
        if (frame.chunkNumber === -1) return;
        set((state) => ({ debug: noteResponseStream(state.debug, now, turnKey(frame.turnId)) }));
        // Audio arriving while the user is holding the button belongs to the reply being talked
        // over: the gateway had already sent it when the interruption reached it. Playing it would
        // undo the flush on the press, since it schedules from now.
        if (!get().micArmed) player.play(frame);
        return;
      }

      case 'thinking': {
        if (!frame.thinkingEnabled) {
          // The indicator is not taken down here: a tool that returns quickly stops thinking well
          // before the first word of the reply has been synthesized, and hiding on the stop frame
          // blanks the bubble again for that gap. The label reverts and the reply's first chunk is
          // what ends it.
          set({ thinkingText: 'Thinking' });
          return;
        }
        set((state) => ({
          thinking: true,
          thinkingText: frame.thinkingText || 'Thinking',
          thinkingIcons: frame.thinkingIcon
            ? [frame.thinkingIcon, ...state.thinkingIcons].slice(0, 3)
            : state.thinkingIcons,
        }));
        return;
      }

      case 'toolPayload': {
        handleToolPayload(frame.toolPayloadType, frame.toolPayload);
        return;
      }

      case 'transcription': {
        handleTranscription(frame.text ?? '');
        return;
      }

      case 'error': {
        const message =
          typeof frame.errorMessage === 'string'
            ? frame.errorMessage
            : JSON.stringify(frame.errorMessage ?? 'Unknown gateway error');
        set({ error: message, streaming: false, thinking: false });
        return;
      }

      case 'modelResponseChunk': {
        if (frame.chunkNumber === -1) {
          endTurn();
          return;
        }

        set((state) => ({ debug: noteResponseStream(state.debug, now, turnKey(frame.turnId)) }));

        // A spoken turn has no send() to open it, so the reply's first chunk is what creates the
        // bubble it streams into.
        if (!get().streaming) beginAssistantTurn();

        // Trimmed at the front because the model's first chunk of a turn routinely leads with a
        // bare newline, sent before any tool has run. It renders to nothing, so keeping it would
        // leave a bubble that is empty but not blank — which is what the indicator stands in for.
        currentResponse = (currentResponse + (frame.chunk ?? '')).trimStart();
        writeAssistantText(currentResponse);

        // The reply is what the indicator was standing in for, so it comes down here rather than
        // on the server's stop frame — but only once there is a word to replace it with.
        if (currentResponse) set({ thinking: false });
        return;
      }

      case 'changeVoiceState': {
        const state = asVoiceMode(frame.state);
        // Unrecognized values are ignored outright, per contract — not even logged as unknown,
        // since the frame type itself is known and handled.
        if (state) set({ voiceMode: state });
        return;
      }

      // Known, deliberately not acted on: what the receiver shows is the receiver's business, and
      // a closing socket is already observable through onclose.
      case 'screenState':
      case 'closeSession':
        return;
    }
  }

  /**
   * Renders a tool result payload as its own block.
   *
   * A tool that ran before the model spoke goes *above* the reply, so the shelf sits over the
   * confirmation about to stream into it. A tool that ran after — the follow-up chips tool ends
   * the turn, so it fires last — goes below the reply it followed.
   */
  function handleToolPayload(type: string | undefined, payload: unknown): void {
    const mapped = toPayloadBlock(type, payload ?? {});
    if (!mapped) {
      if (type) noteUnknown('unknownPayloadTypes', type);
      return;
    }

    const block = toMessage(mapped);
    if (!block) return;

    set((state) => {
      const last = state.messages[state.messages.length - 1];
      // Only an *empty* placeholder is jumped ahead of. Once the reply has streamed into it, the
      // block belongs after it.
      if (last && last.role === 'assistant' && last.kind === 'text' && !last.text) {
        const messages = [...state.messages];
        messages.splice(messages.length - 1, 0, block);
        return { messages };
      }
      return { messages: [...state.messages, block] };
    });
  }

  /** Null for a payload that parsed but has nothing worth a block of its own. */
  function toMessage(mapped: ToolPayloadBlock): Message | null {
    if (mapped.kind === 'choice') {
      if (!mapped.choice.options.length) return null;
      return { id: nextId(), role: 'assistant', kind: 'choice', choice: mapped.choice };
    }

    const { cards, suggestedChips, liveCompanion } = mapped.results;
    if (!cards.length && !suggestedChips.length && !liveCompanion) return null;
    return { id: nextId(), role: 'assistant', kind: 'results', results: mapped.results };
  }

  /** Records a type we could not render, once, so the drawer can surface protocol drift. */
  function noteUnknown(field: 'unknownFrameTypes' | 'unknownPayloadTypes', type: string): void {
    set((state) => (state[field].includes(type) ? state : { [field]: [...state[field], type] }));
  }

  /**
   * Shows what the server has recognized so far, in the user's own bubble. The first
   * transcription of an utterance creates that bubble and every later one rewrites it, which is
   * what the message's replace-not-append semantics ask for.
   */
  function handleTranscription(text: string): void {
    if (!text) return;

    set((state) => {
      const bubble: TextMessage = { id: nextId(), role: 'user', kind: 'text', text };
      if (transcribing) {
        return { messages: [...state.messages.slice(0, -1), bubble], transcript: text };
      }
      return {
        messages: [...state.messages, bubble],
        transcript: text,
        // Stands in for the send() a typed turn would have made.
        thinking: false,
        thinkingText: 'Thinking',
        thinkingIcons: [],
      };
    });
    transcribing = true;
  }

  /** Closes the socket and puts every handle back where {@link connect} expects to find it. */
  function teardown(): void {
    const closing = socket;
    socket = null;

    if (closing) {
      // Detached before closing: this close is one we asked for, and its onclose would fire
      // asynchronously, after a new connection had already set connection: 'connecting'.
      closing.onopen = null;
      closing.onclose = null;
      closing.onmessage = null;
      closing.onerror = null;
      closing.close();
    }

    get().stopMic();
    currentResponse = '';
    transcribing = false;
    player.stop();

    set({
      connection: 'idle',
      streaming: false,
      thinking: false,
      // A session id belongs to the server that issued it and the identity it was signed in
      // with, so a reconnect asks for a new one rather than resuming across that change.
      sessionId: null,
      // The model belonged to that session; the selector's own value is what the next one uses.
      sessionModelId: null,
      // Likewise the voice: the next session speaks with whatever the selector names at sign-in.
      sessionTts: null,
    });
  }

  function persist(): void {
    const {
      endpoint,
      signIn,
      voiceMode,
      dropAbandonedText,
      modelId,
      ttsProvider,
      ttsModel,
      ttsVoice,
    } = get();
    saveSettings({
      endpoint,
      signIn,
      voiceMode,
      dropAbandonedText,
      modelId,
      ttsProvider,
      ttsModel,
      ttsVoice,
    });
  }

  /**
   * Discards everything the closed session produced. Signing in again yields a new session id, so
   * the server no longer remembers any of it, and a history the backend has forgotten is a trap when
   * what is being tested is multi-turn behaviour — turns and frames included, since their timings
   * belong to a session that no longer exists.
   */
  function clearRun(): void {
    set({
      messages: [],
      transcript: null,
      thinkingIcons: [],
      // The next session opens on an empty transcript, which is where the prompt belongs again.
      introDismissed: false,
      debug: EMPTY_DEBUG_STATE,
      frames: [],
      framesDropped: 0,
      audioFramesSent: 0,
    });
  }

  return {
    connection: 'idle',
    deviceMode: null,
    sessionId: null,
    sessionModelId: null,
    sessionTts: null,
    endpoint: initial.endpoint,
    signIn: initial.signIn,
    voiceMode: initial.voiceMode,
    dropAbandonedText: initial.dropAbandonedText,
    modelId: initial.modelId,
    ttsProvider: initial.ttsProvider,
    ttsModel: initial.ttsModel,
    ttsVoice: initial.ttsVoice,
    ttsCatalog: [],
    ttsCatalogStatus: 'idle',
    ttsCatalogError: null,

    messages: [],
    introDismissed: false,
    streaming: false,
    thinking: false,
    thinkingText: 'Thinking',
    thinkingIcons: [],
    transcript: null,

    muted: false,
    micArmed: false,
    micLive: false,
    micLevel: 0,

    error: null,
    debug: EMPTY_DEBUG_STATE,
    frames: [],
    framesDropped: 0,
    audioFramesSent: 0,
    unknownFrameTypes: [],
    unknownPayloadTypes: [],

    connect(mode) {
      if (get().connection !== 'idle') return;

      // A socket that dropped mid-interruption leaves a tail that will now never arrive, and the
      // count has to go with it or the new session's first reply is dropped instead.
      abandonedTurns = 0;
      replyInFlight = false;
      activeTurnId = null;
      micTurnId = null;
      // Re-latched per connection: the endpoint is editable, and a reconnect is how the console is
      // pointed at a different backend, which may not echo what this one did.
      turnIdsEchoed = false;
      // `nextTurn` deliberately survives this: turn numbering belongs to the session the server
      // keeps, not to the socket, and it is reset on the sign-in response that says the session is
      // a new one.

      set({ deviceMode: mode, connection: 'connecting', error: null });

      // From the connect click, which is the user gesture the autoplay policy wants.
      player.initialize();
      player.setMuted(get().muted);

      let ws: WebSocket;
      try {
        ws = new WebSocket(resolveEndpoint(get().endpoint));
      } catch (error) {
        // The endpoint is editable, and the constructor throws outright on a malformed URL rather
        // than reporting it through onclose.
        set({
          connection: 'idle',
          error: `That endpoint is not a valid WebSocket URL: ${String(error)}`,
        });
        return;
      }
      socket = ws;

      ws.onopen = () => {
        const details = get().signIn;
        // Which model answers. A named one goes in on every sign-in rather than only when it differs
        // from the deployment's own default: a run has to say what it was run on, and the frame log
        // is where a tester reads that back. The backend-default choice — and a stored value the
        // gateway would refuse — names none, and leaves `metadata.llm` off the frame (and `metadata`
        // too, when no voice is named either).
        const model = parseModelId(get().modelId);
        set({
          sessionModelId:
            model === null ? DEFAULT_MODEL_ID : formatModelId(model.provider, model.model),
        });

        // Which voice speaks, on the same terms: the chosen provider/model/voice goes in on every
        // sign-in so the run says what it spoke with. Built from the stored selection directly rather
        // than the catalogue, so a sign-in before the catalogue has loaded still carries the chosen
        // voice instead of silently falling back to the backend default; the backend validates it.
        const { ttsProvider, ttsModel, ttsVoice, ttsCatalog } = get();
        const tts = selectionFromStored(ttsProvider, ttsModel, ttsVoice);
        set({
          sessionTts: ttsSummary(ttsCatalog, ttsProvider, ttsModel, ttsVoice),
        });

        get().sendFrame({
          // Sign in before anything else — the server rejects messages until the session is
          // signed in. signInDetails is a required part of the contract; the gateway hands it to
          // any tool whose request payload asks for account info.
          type: 'signInRequest',
          deviceMode: mode,
          voiceMode: get().voiceMode,
          signInDetails: {
            ...details,
            // Its own receiver, there being no host: the contract makes the two equal outside a
            // multi-receiver household.
            hostReceiverId: details.receiverId,
          },
          ...(model === null && tts === null
            ? {}
            : {
                metadata: {
                  ...(model === null ? {} : { llm: model }),
                  ...(tts === null ? {} : { tts }),
                },
              }),
        });
      };

      ws.onmessage = (event: MessageEvent<string>) => {
        const frame = parseFrame(event.data);
        if (!frame) {
          logFrame('in', 'malformed', false, String(event.data).slice(0, 2000));
          return;
        }
        logFrame('in', frame.type, isKnownFrameType(frame.type), summarizeFrame(frame));
        if (isKnownFrameType(frame.type)) handleFrame(frame);
        else noteUnknown('unknownFrameTypes', frame.type);
      };

      ws.onerror = () => {
        if (socket !== ws) return;
        set({ error: 'The WebSocket connection failed. Check the endpoint and try again.' });
      };

      ws.onclose = (event) => {
        if (socket !== ws) return;
        socket = null;
        get().stopMic();
        set((state) => ({
          connection: 'idle',
          streaming: false,
          thinking: false,
          sessionId: null,
          sessionModelId: null,
          error:
            state.error ??
            (event.wasClean ? null : `The gateway closed the connection (code ${event.code}).`),
        }));
      };
    },

    /**
     * Drops the connection and signs in again, picking up an edited endpoint, receiver identity or
     * model. None of them can take effect on a live socket: the endpoint is fixed once the socket is
     * constructed, and the rest is only ever sent in the sign-in handshake.
     */
    reconnect() {
      const mode = get().deviceMode ?? 'DISH';
      teardown();
      clearRun();
      get().connect(mode);
    },

    /**
     * Leaves the session for the start screen: the same clean slate a reconnect makes, minus the
     * reconnect. `deviceMode: null` is what the app reads as "not in a session yet", so this is also
     * how a tester gets back to choosing a platform and a model.
     */
    leaveSession() {
      teardown();
      clearRun();
      set({ deviceMode: null, error: null });
    },

    send(text) {
      const trimmed = text.trim();
      if (!trimmed || get().connection !== 'open') return;

      // Barge-in by typing. The gateway abandons the running turn as soon as a new request arrives,
      // but its audio pacing runs ahead of the speaker and its last frames are already on the wire.
      abandonStreamingReply();

      beginAssistantTurn(trimmed);

      // Shown from the moment the turn is sent rather than from the server's first thinking frame.
      // The server brackets tool execution only, so the model's own first-token latency — seconds
      // of it, with a guardrail evaluation in front — would otherwise sit behind an empty bubble.
      set({ thinking: true });

      // Only the new message: the server keeps the conversation's memory per session, keyed by the
      // session id the sign-in handshake returned.
      activeTurnId = newTurnId();
      get().sendFrame({ type: 'chatRequest', turnId: activeTurnId, message: trimmed });
    },

    dismissIntro() {
      if (get().introDismissed) return;
      set({ introDismissed: true });
    },

    sendFrame(frame) {
      if (socket?.readyState !== WebSocket.OPEN) return;
      socket.send(JSON.stringify(frame));

      // Mic frames arrive fifteen times a second; logging each one would bury everything else, so
      // they are counted instead.
      if (frame.type === 'audioInput' && frame.chunkNumber !== -1) {
        set((state) => ({ audioFramesSent: state.audioFramesSent + 1 }));
        return;
      }
      logFrame('out', frame.type, true, summarizeFrame(frame));
    },

    setEndpoint(endpoint) {
      set({ endpoint });
      persist();
      // The catalogue is the chosen endpoint's own, so a change refetches it for the selects.
      get().loadTtsCatalog();
    },

    resetEndpoint() {
      get().setEndpoint(deployedEndpoint());
    },

    patchSignIn(key, value) {
      set({ signIn: { ...get().signIn, [key]: value } });
      persist();
    },

    setVoiceMode(voiceMode) {
      set({ voiceMode });
      persist();
    },

    setDropAbandonedText(dropAbandonedText) {
      set({ dropAbandonedText });
      persist();
    },

    setModelId(modelId) {
      set({ modelId });
      persist();
    },

    setTts({ provider, model, voice }) {
      set({ ttsProvider: provider, ttsModel: model, ttsVoice: voice });
      persist();
    },

    loadTtsCatalog() {
      // One fetch at a time: a second call while one is in flight is a no-op.
      if (get().ttsCatalogStatus === 'loading') {
        return;
      }
      set({ ttsCatalogStatus: 'loading', ttsCatalogError: null });
      const endpoint = resolveEndpoint(get().endpoint);
      const url = (() => {
        try {
          return catalogUrl(endpoint);
        } catch {
          return endpoint;
        }
      })();
      fetchTtsCatalog(endpoint)
        .then((catalog) => {
          set({ ttsCatalog: catalog, ttsCatalogStatus: 'ready', ttsCatalogError: null });
        })
        .catch((error: unknown) => {
          const reason = error instanceof Error ? error.message : String(error);
          console.warn('Could not load TTS catalog from', url, error);
          set({
            ttsCatalog: [],
            ttsCatalogStatus: 'error',
            ttsCatalogError: `${url} — ${reason}`,
          });
        });
    },

    toggleMute() {
      const muted = !get().muted;
      set({ muted });
      player.setMuted(muted);
    },

    dismissError() {
      set({ error: null });
    },

    clearFrames() {
      set({ frames: [], framesDropped: 0 });
    },

    /**
     * Opens the microphone and streams it to the gateway as audioInput frames. There is no start
     * message: the first frame is what opens the server's recognition stream, and {@link stopMic}'s
     * end marker is what closes it.
     */
    startMic() {
      if (get().micArmed || get().connection !== 'open') return;

      audioChunkNumber = 0;
      utterancePeak = 0;
      micTurnId = newTurnId();
      // The press is the interruption, so the utterance is the turn the conversation is written from
      // from here on — well before anything has been recognized, which is exactly when the reply
      // being talked over is still arriving under the previous id.
      activeTurnId = micTurnId;
      // Barge-in, from this side. The gateway abandons the turn once it sees the audio, but its
      // pacing runs ahead of the speaker, so the reply keeps playing here until we drop what is
      // already scheduled. Doing it on the press is what makes talking over the assistant feel
      // immediate rather than as slow as the round trip.
      //
      // Deliberately not conditional on the gateway also abandoning it: the press is the user
      // deciding the reply is over, and a backend with barge-in off goes on producing a reply this
      // client has stopped speaking and stopped rendering either way.
      abandonStreamingReply();
      set((state) => ({
        micArmed: true,
        micLevel: 0,
        // Speaking is the interaction the opening prompt was asking for.
        introDismissed: true,
        // Stamped on the press, not once the device is open: this is what starts a turn, and the
        // permission prompt can sit between the two for seconds the user did not wait on us.
        debug: recordClient(state.debug, CLIENT_EVENTS.speechStart, Date.now(), turnKey(micTurnId)),
      }));

      void startMicCapture((frame) => {
        const level = frameLevel(frame);
        if (level > utterancePeak) utterancePeak = level;
        set({ micLevel: level });
        // Armed implies a minted turn; the check is what lets the id be null between utterances
        // rather than carrying a sentinel number that turn 0 could be mistaken for.
        if (micTurnId === null) return;
        get().sendFrame({
          type: 'audioInput',
          turnId: micTurnId,
          chunkNumber: audioChunkNumber++,
          base64Audio: encodeFrame(frame),
          encoding: MIC_ENCODING,
          sampleRate: MIC_SAMPLE_RATE,
          bitsPerSample: MIC_BITS_PER_SAMPLE,
          channels: MIC_CHANNELS,
        });
      })
        .then((mic) => {
          // Released during the tap, or while getUserMedia was opening the device: stop has
          // already sent the end marker, so all that is left is to hand the device back.
          if (!get().micArmed) {
            mic.stop();
            return;
          }
          micStream = mic;
          set({ micLive: true });
        })
        .catch((error: unknown) => {
          set({
            micArmed: false,
            micLive: false,
            error: `The microphone is unavailable: ${String(error)}`,
          });
        });
    },

    /** Closes the microphone and tells the gateway the utterance is over. */
    stopMic() {
      if (!get().micArmed) return;

      const deviceLabel = micStream?.deviceLabel;
      const framesSent = audioChunkNumber;
      micStream?.stop();
      micStream = null;
      set((state) => ({
        micArmed: false,
        micLive: false,
        micLevel: 0,
        debug: recordClient(state.debug, CLIENT_EVENTS.speechEnd, Date.now(), turnKey(micTurnId)),
      }));

      // chunkNumber -1 is the end marker: it flushes a final recognition result at once, where
      // waiting for the server to detect the silence costs the better part of a second.
      if (micTurnId !== null) {
        get().sendFrame({
          type: 'audioInput',
          turnId: micTurnId,
          chunkNumber: -1,
          base64Audio: '',
        });
      }

      // An utterance of nothing but zeros. The gateway will open a recognition stream, find nothing
      // in it and close it again without a transcript, so the turn would otherwise end as an
      // unexplained silence — indistinguishable, from the outside, from a backend that dropped it.
      if (framesSent > 0 && utterancePeak === 0) {
        set({
          error: `Only silence from ${deviceLabel ?? 'the microphone'} — check the browser's input device.`,
        });
      }
    },
  };
});
